import { ObjectAttributesEditor, useObjectAttributes } from '@/features/objects';
import { useAnalytics } from '@/services/analytics';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { filteredAttrsWithIdentifiers } from '@/services/db/object/entity';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { useObjectEditorGuard } from '../use-object-editor-guard';
import { useUpdateObjectFlow } from '../use-update-object-flow';
import { useAttributesBreadcrumbs } from './use-attributes-breadcrumbs';
import { useObjectDetail } from './context';

function ObjectAttributesEditForm() {
  const detail = useObjectDetail();
  const { record, objectType, workspaceId, canConfigureObjects } = detail;
  const { track } = useAnalytics();
  const environment = useEnvironmentOptional().data?.environment;
  useAttributesBreadcrumbs({ withEdit: true });

  const editor = useObjectAttributes({ initialAttrs: filteredAttrsWithIdentifiers(record), isNew: false });
  const flow = useUpdateObjectFlow({
    objectType,
    workspaceId,
    record,
    editor,
    attributesHref: detail.hrefs.tabs.attributes,
  });

  const save = useObjectEditorGuard({
    editor,
    flow,
    canConfigureObjects,
    failureMessage: 'Object update failed',
  });

  return (
    <ObjectAttributesEditor
      editor={editor}
      sentAttributes={objectType.sent_attributes ?? []}
      hasMoreAttributes={environment?.more_attributes === true}
      isMultiIdentifier={detail.isMultiIdentifier}
      isSaving={flow.isSaving}
      canConfigureObjects={canConfigureObjects}
      onSave={save}
      onReset={flow.discard}
      onCreateAttribute={() =>
        void track('objects', 'objectNewAttributeCreateClicked', {
          cio_object_id: record.attributes.cio_object_id,
          object_id: record.attributes.object_id,
        })
      }
    />
  );
}

export function ObjectAttributesEdit() {
  const { hrefs } = useObjectDetail();

  return (
    <ProtectedRoute permission="journeys/objects/edit" redirectTo={hrefs.tabs.attributes}>
      <ObjectAttributesEditForm />
    </ProtectedRoute>
  );
}
